/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Stack } from '@mui/system';
import React from 'react';
import { useSelector } from 'react-redux';
import { usePrevious } from '@opengeoweb/shared';
import type { CoreAppStore, uiTypes } from '@opengeoweb/store';
import { uiSelectors } from '@opengeoweb/store';
import { Options } from '@opengeoweb/theme';
import { MapControlButton } from '@opengeoweb/webmap-react';
import { GetFeatureInfoButtonConnect } from './FeatureInfo';
import { MeasureToolButton } from './MeasureTool';
import { useCoreTranslation } from '../../utils/i18n';

interface MapOptionsButtonProps {
  mapId: string;
  getFeatureInfo?: boolean;
  measureTool?: boolean;
}

export const MapOptionsButtonConnect: React.FC<MapOptionsButtonProps> = ({
  mapId,
  getFeatureInfo = false,
  measureTool = false,
}: MapOptionsButtonProps) => {
  const { t } = useCoreTranslation();
  const [isMapOptionsOpen, setIsMapOptionsOpen] = React.useState(false);
  const [isMeasureToolActive, setIsMeasureToolActive] = React.useState(false);

  const gfiType = `getfeatureinfo-${mapId}`;
  const isFeatureInfoActive = useSelector((store: CoreAppStore) =>
    uiSelectors.getisDialogOpen(store, gfiType as uiTypes.DialogType),
  );

  const previousIsMeasureToolActive = usePrevious(isMeasureToolActive);
  const previousIsFeatureInfoActive = usePrevious(isFeatureInfoActive);

  React.useEffect(() => {
    if (!isMapOptionsOpen && (isMeasureToolActive || isFeatureInfoActive)) {
      setIsMapOptionsOpen(true);
    }
    if (
      isMapOptionsOpen &&
      !isMeasureToolActive &&
      !isFeatureInfoActive &&
      (previousIsMeasureToolActive || previousIsFeatureInfoActive)
    ) {
      setIsMapOptionsOpen(false);
    }
  }, [
    isFeatureInfoActive,
    isMapOptionsOpen,
    isMeasureToolActive,
    previousIsFeatureInfoActive,
    previousIsMeasureToolActive,
  ]);

  return getFeatureInfo && measureTool ? (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        marginBottom: 1,
      }}
    >
      <MapControlButton
        data-testid="map-options-button"
        onClick={() => setIsMapOptionsOpen(!isMapOptionsOpen)}
        optionsActive={
          isMapOptionsOpen || isMeasureToolActive || isFeatureInfoActive
        }
        shouldShowExtraOptionsIndicator={true}
        title={t('map-options-button')}
      >
        <Options />
      </MapControlButton>
      <Stack
        direction="row"
        spacing={1}
        sx={{ display: isMapOptionsOpen ? 'flex' : 'none' }}
      >
        {!!getFeatureInfo && <GetFeatureInfoButtonConnect mapId={mapId} />}
        {!!measureTool && (
          <MeasureToolButton
            isActive={isMeasureToolActive}
            setIsActive={setIsMeasureToolActive}
          />
        )}
      </Stack>
    </Stack>
  ) : (
    <>
      {!!getFeatureInfo && <GetFeatureInfoButtonConnect mapId={mapId} />}
      {!!measureTool && (
        <MeasureToolButton
          isActive={isMeasureToolActive}
          setIsActive={setIsMeasureToolActive}
        />
      )}
    </>
  );
};
